// Voice Selection Imports

import IconChrome from '@listening/assets/astro/icons/icon-chrome.svg';
import IconNotion from '@listening/assets/astro/icons/icon-document.svg';
import IconPasteText from '@listening/assets/astro/icons/icon-paste-text.svg';
import IconPdf from '@listening/assets/astro/icons/icon-pdf.svg';
import IconPdfUpload from '@listening/assets/astro/icons/icon-pdf-upload.svg';
import IconScan from '@listening/assets/astro/icons/icon-scan.svg';
import IconWebpage from '@listening/assets/astro/icons/icon-webpage.svg';
import IconWordDocs from '@listening/assets/astro/icons/icon-word-docs.svg';
import featuresFrameAmanda from '@listening/assets/astro/images/new-design-25/homepage/features-avatar-1.png';
import featuresFrameAnna from '@listening/assets/astro/images/new-design-25/homepage/features-avatar-2.png';
import featuresFramePeter from '@listening/assets/astro/images/new-design-25/homepage/features-avatar-3.png';
import featuresFrameSimon from '@listening/assets/astro/images/new-design-25/homepage/features-avatar-4.png';
// Upload Options Imports
import * as m from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';
import { useAtomValue } from '@effect/atom-react';
import { voiceCatalogAtom } from '@listening/frontend/query/voices';
import type { KnownVoiceDisplayName } from '@listening/api-contract/schemas';
import VoiceSelectionBlock from './VoiceSelectionBlock';

// Voice Selection Logic
// Instead of using voicesMetadataMap, let's directly use the voices we have
// samples for. Keys are checked against the canonical KnownVoiceDisplayName union, so
// renaming/removing a featured voice in VOICE_DEFS is a compile error here.
const featuredVoiceDataEn = {
  Amy: featuresFrameAmanda,
  Scarlett: featuresFrameAnna,
  Dan: featuresFramePeter,
  Will: featuresFrameSimon,
} satisfies Partial<Record<KnownVoiceDisplayName, typeof featuresFrameAmanda>>;

const featuredVoiceDataEs = {
  Carmen: featuresFrameAnna,
  Diego: featuresFrameSimon,
  Pablo: featuresFramePeter,
} satisfies Partial<Record<KnownVoiceDisplayName, typeof featuresFrameAmanda>>;

function getFeaturedVoiceData() {
  return getLocale() === 'es' ? featuredVoiceDataEs : featuredVoiceDataEn;
}

// Upload Options Data
const uploadOptions = [
  { icon: IconPdf, label: () => m.homepage_features_upload_pdf() },
  { icon: IconWordDocs, label: () => m.homepage_features_upload_word() },
  { icon: IconNotion, label: () => m.homepage_features_upload_mobi_epub() },
  { icon: IconPasteText, label: () => m.homepage_features_upload_paste() },
  { icon: IconPdfUpload, label: () => m.homepage_features_upload_pdf_upload() },
  { icon: IconWebpage, label: () => m.homepage_features_upload_website() },
  { icon: IconChrome, label: () => m.common_chrome_extension() },
  { icon: IconScan, label: () => m.homepage_features_upload_scan() },
];

export function RowOneFeatures() {
  const allVoices = useAtomValue(voiceCatalogAtom);
  const featuredVoiceData = getFeaturedVoiceData();
  const featuredVoices = allVoices
    .filter((voice) => Object.keys(featuredVoiceData).includes(voice.name))
    .map((v) => ({
      ...v,
      image_url: featuredVoiceData[v.name as keyof typeof featuredVoiceData],
    }));

  return (
    <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
      {/* <!-- Voice Selection Section --> */}
      <div
        aria-labelledby='voice-selection-title'
        className='flex flex-col rounded-xl bg-azure-525/10 p-4 sm:rounded-2xl sm:p-6 lg:col-span-2 lg:p-8'
        role='region'
      >
        {/* <!-- Main content area with React component --> */}
        <VoiceSelectionBlock voices={featuredVoices} />

        <div className='mt-12'>
          <h3
            className='mb-3 text-2xl font-black text-navy-220 sm:text-2xl lg:mb-4 lg:text-3xl'
            id='voice-selection-title'
          >
            {m.homepage_features_voice_title()}
          </h3>
          <p className='text-xs font-medium text-navy-220/70 sm:text-sm'>
            {m.homepage_features_voice_description()}
          </p>
        </div>
      </div>

      {/* <!-- Upload Options Section --> */}
      <div
        aria-labelledby='upload-options-title'
        className='flex flex-col rounded-xl bg-azure-525/10 p-4 sm:rounded-2xl sm:p-6 lg:p-8'
        role='region'
      >
        {/* <!-- Main content area --> */}
        <div className='grow'>
          <div
            aria-label='Upload options'
            className='grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 lg:grid-cols-2 xl:grid-cols-4'
            role='list'
          >
            {uploadOptions.map((option) => (
              <div
                className='group flex flex-col items-center gap-2 sm:gap-3'
                key={option.label()}
                role='listitem'
              >
                <div className='flex size-16 min-w-[64px] items-center justify-center rounded-full bg-white/10 transition-all duration-500 group-hover:-translate-y-1 group-hover:bg-white/20 group-hover:shadow-lg sm:size-18 lg:size-20 xl:size-16'>
                  <img
                    alt='icon'
                    className='size-8 transition-transform duration-500 group-hover:scale-110 sm:size-9 lg:size-9 xl:size-7'
                    role='presentation'
                    src={option.icon}
                  />
                </div>
                <span className='text-center text-xs font-bold text-navy-220 transition-colors duration-500 sm:text-sm'>
                  {option.label()}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className='mt-12'>
          <h3
            className='mb-3 text-2xl font-black text-navy-220 lg:mb-4'
            id='upload-options-title'
          >
            {m.homepage_features_upload_title()}
          </h3>
          <p className='text-xs font-medium text-navy-220/70 sm:text-sm'>
            {m.homepage_features_upload_description()}
          </p>
        </div>
      </div>
    </div>
  );
}
